import React, { useCallback, useEffect, useState } from 'react';
import { NextComponentType, NextPageContext } from 'next';
import { PageProps } from 'types/page';
import clsx from 'clsx';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import useStyles from './styles';
import { FullWidthSlotContext } from './FullWidthSlotContext';
import { AdSpace } from 'molecules';
import { WithLayoutAdUnits } from 'config/Ads';

export type LayoutContentWidth = 'full' | 'wide' | 'md' | 'sm';

export interface WithLayoutOptions {
  contentWidth?: LayoutContentWidth;
  /** When true, outer column gets 24px top margin on mobile and 48px on `md+` (no bottom margin). */
  margin?: boolean;
  /** When true, inner content row gets 24px left/right padding on mobile and desktop (inset for `width: 100%`). */
  gutter?: boolean;
  /** When true, renders a top ad (320×50 below 970px wide, 970×250 on desktop). */
  topAd?: boolean;
  /** When true, renders a bottom ad (300×250 below 970px wide, 970×250 on desktop). */
  bottomAd?: boolean;
}

const widthClassKey: Record<
  LayoutContentWidth,
  'layoutWidthFull' | 'layoutWidthWide' | 'layoutWidthMd' | 'layoutWidthSm'
> = {
  full: 'layoutWidthFull',
  wide: 'layoutWidthWide',
  md: 'layoutWidthMd',
  sm: 'layoutWidthSm',
};

/**
 * HOC that provides consistent layout structure for page components.
 * Full-width slot above a constrained column. Use {@link useRegisterFullWidthSlot}
 * for full-bleed UI (e.g. steppers).
 *
 * @param options - Layout options (`contentWidth`, `gutter`, `margin`, optional ad slots).
 */
export default function withLayout<T>(
  Component: NextComponentType<NextPageContext, any, PageProps & T>,
  options: WithLayoutOptions = {},
) {
  const {
    contentWidth = 'wide',
    margin = false,
    gutter = true,
    topAd = false,
    bottomAd = false,
  } = options;

  const WrappedComponent = (props: PageProps & T) => {
    const { classes } = useStyles();
    const widthClass = classes[widthClassKey[contentWidth]];
    const [fullWidthSlot, setFullWidthSlot] = useState<React.ReactNode>(null);
    const setFullWidthSlotStable = useCallback((node: React.ReactNode) => {
      setFullWidthSlot(node);
    }, []);

    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

    return (
      <FullWidthSlotContext.Provider value={setFullWidthSlotStable}>
        <Stack
          direction="column"
          width="100%"
          alignItems="stretch"
          className={clsx(
            classes.layoutContainer,
            margin && classes.layoutContainerMargin,
          )}
          data-hoc="withLayout"
        >
          {fullWidthSlot ? (
            <Box width="100%" data-withlayout="full-width-slot">
              {fullWidthSlot}
            </Box>
          ) : null}
          {topAd ? (
            <Box className={classes.adSlotSection} data-withlayout="ad-top">
              <AdSpace
                type="layout-top-banner"
                adUnit={WithLayoutAdUnits.top}
                size="responsive"
                slotId="withlayout-top"
              />
            </Box>
          ) : null}
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            className={clsx(classes.layout, widthClass, gutter && classes.layoutGutter)}
            data-withlayout="layout"
          >
            <Component {...props} />
          </Stack>
          {bottomAd ? (
            <Box className={classes.adSlotSection} data-withlayout="ad-bottom">
              <AdSpace
                type="banner-ad"
                adUnit={WithLayoutAdUnits.bottom}
                size="responsive"
                slotId="withlayout-bottom"
              />
            </Box>
          ) : null}
        </Stack>
      </FullWidthSlotContext.Provider>
    );
  };

  const componentName = Component.displayName || Component.name || 'Component';
  WrappedComponent.displayName = `withLayout(${componentName})`;

  return WrappedComponent;
}
